import EditorialLayout, { editorialStyles as styles } from '../../components/EditorialLayout'

const strings = [
  { string: '6th', note: 'E2', frequency: '82.41 Hz', width: 6 },
  { string: '5th', note: 'A2', frequency: '110.00 Hz', width: 5 },
  { string: '4th', note: 'D3', frequency: '146.83 Hz', width: 4 },
  { string: '3rd', note: 'G3', frequency: '196.00 Hz', width: 3 },
  { string: '2nd', note: 'B3', frequency: '246.94 Hz', width: 2 },
  { string: '1st', note: 'E4', frequency: '329.63 Hz', width: 1 },
]

export default function GuitarStringFrequencyChart() {
  return (
    <EditorialLayout
      title='Guitar String Frequency Chart: Standard Tuning Explained'
      description='Use this standard guitar tuning chart to match every string with its note, octave, and target frequency, from low E2 to high E4.'
      path='/guides/guitar-string-frequency-chart'
      eyebrow='Tuning basics'
      updated='August 10, 2026'
      article
    >
      <p>
        In standard tuning, a six-string guitar is tuned E–A–D–G–B–E from the
        thickest string to the thinnest. The two E strings share a note name but
        sit two octaves apart, so their frequencies are very different.
      </p>

      <div className={styles.strings} aria-label='Six guitar strings from low E to high E'>
        {strings.map((item) => (
          <div className={styles.stringRow} key={item.string}>
            <span className={styles.note}>{item.note}</span>
            <span
              className={styles.stringLine}
              style={{ height: `${item.width}px` }}
              aria-hidden='true'
            ></span>
            <span className={styles.frequency}>{item.frequency}</span>
          </div>
        ))}
      </div>

      <table>
        <thead>
          <tr>
            <th>String</th>
            <th>Note</th>
            <th>MIDI</th>
            <th>Frequency</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>6th — thickest</td><td>E2</td><td>40</td><td>82.41 Hz</td></tr>
          <tr><td>5th</td><td>A2</td><td>45</td><td>110.00 Hz</td></tr>
          <tr><td>4th</td><td>D3</td><td>50</td><td>146.83 Hz</td></tr>
          <tr><td>3rd</td><td>G3</td><td>55</td><td>196.00 Hz</td></tr>
          <tr><td>2nd</td><td>B3</td><td>59</td><td>246.94 Hz</td></tr>
          <tr><td>1st — thinnest</td><td>E4</td><td>64</td><td>329.63 Hz</td></tr>
        </tbody>
      </table>

      <h2>Why the octave number matters</h2>
      <p>
        A note name repeats every octave. Moving up one octave doubles the
        frequency; moving down one octave halves it. That is why the low E2 at
        82.41 Hz, E3 at 164.81 Hz, and high E4 at 329.63 Hz all sound like E,
        while clearly occupying different registers.
      </p>

      <div className={styles.example}>
        <h3>Example: the tuner says E, but the string is still wrong</h3>
        <p>
          If the sixth string is close to 164.81 Hz, the tuner may label it E3.
          That is an E, but it is one octave above the correct E2 target and the
          string is under dangerously high tension. Stop tightening and tune
          down toward 82.41 Hz.
        </p>
      </div>

      <h2>How frequencies are calculated</h2>
      <p>
        Modern tuners normally use twelve-tone equal temperament. Starting from
        A4 = 440 Hz, each semitone changes frequency by the twelfth root of two.
        The relationship can be written as:
      </p>
      <div className={styles.callout}>
        <p><code>frequency = 440 × 2^(semitones from A4 ÷ 12)</code></p>
        <p>
          A2 is 24 semitones below A4, so its frequency is
          440 × 2<sup>−2</sup> = 110 Hz.
        </p>
      </div>

      <h2>Frequency versus cents</h2>
      <p>
        Frequency is measured in hertz, while cents describe the musical
        distance between two pitches. One semitone contains 100 cents. A tuner
        can therefore show how far a played frequency sits from its target even
        though the number of hertz represented by one cent changes across the
        instrument’s range.
      </p>
      <table>
        <thead>
          <tr><th>Reading for A2</th><th>Approximate position</th><th>What to do</th></tr>
        </thead>
        <tbody>
          <tr><td>108.42 Hz</td><td>−25 cents</td><td>Tighten slightly</td></tr>
          <tr><td>110.00 Hz</td><td>0 cents</td><td>In tune</td></tr>
          <tr><td>111.60 Hz</td><td>+25 cents</td><td>Loosen slightly</td></tr>
        </tbody>
      </table>

      <h2>A reliable tuning sequence</h2>
      <ol>
        <li>Start with the sixth string and work toward the first: E2, A2, D3, G3, B3, E4.</li>
        <li>Pluck one string at a time with a moderate, consistent attack.</li>
        <li>Wait for the initial sharp transient to settle before judging the reading.</li>
        <li>Approach the target from below whenever possible, which helps remove slack around the tuning post.</li>
        <li>Repeat the full sequence because changing one string slightly changes overall neck and bridge tension.</li>
      </ol>

      <h2>Does acoustic and electric guitar use the same chart?</h2>
      <p>
        Yes. A standard six-string acoustic guitar and a standard six-string
        electric guitar use the same E2–A2–D3–G3–B3–E4 targets. String material,
        scale length, and pickup type affect tone and feel, not the intended
        standard frequencies.
      </p>

      <h2>Frequently asked questions</h2>
      <h3>Why does a plucked note move after the first moment?</h3>
      <p>
        A firm pick attack briefly stretches the string and can make the first
        reading sharp. As vibration decays, the pitch settles. Use a moderate
        attack and judge the stable part of the note.
      </p>
      <h3>Should every guitar use A4 = 440 Hz?</h3>
      <p>
        A4 = 440 Hz is the most common concert-pitch reference and the default
        for this tuner. An ensemble may deliberately use another reference, but
        every player must use the same one.
      </p>

      <div className={styles.related}>
        <h2>Put the chart into practice</h2>
        <p>
          Open the <a href='/acoustic-guitar-tuning'>acoustic guitar tuner</a>{' '}
          or learn <a href='/guides/how-online-guitar-tuner-works'>how an online tuner turns frequency into a note</a>.
        </p>
      </div>
    </EditorialLayout>
  )
}
